import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@app/ui';
import { FadeIn, PageTransition } from '@app/ui/motion';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { RouteErrorComponent } from '../../components/route-error';
import { api } from '../../lib/api';

export const Route = createFileRoute('/admin/audit-logs')({
  validateSearch: z.object({
    action: z.string().optional(),
    page: z.coerce.number().default(1),
  }),
  loaderDeps: ({ search }) => search,
  loader: async ({ context, deps }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    await context.queryClient.ensureQueryData(
      serverApi.admin.auditLogs.queryOptions({
        input: { action: deps.action, page: deps.page },
      }),
    );
  },
  errorComponent: ({ error }) => <RouteErrorComponent error={error} />,
  component: AuditLogs,
});

function AuditLogs() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data } = useSuspenseQuery(
    api.admin.auditLogs.queryOptions({
      input: { action: search.action, page: search.page },
    }),
  );

  const { logs, total, page, totalPages } = data;

  return (
    <PageTransition>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Audit Logs ({total})</h1>
        <Select
          value={search.action || 'all'}
          onValueChange={(v) =>
            navigate({ search: { action: v === 'all' ? undefined : v, page: 1 } })
          }
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="user.login">Login</SelectItem>
            <SelectItem value="user.logout">Logout</SelectItem>
            <SelectItem value="deal.create">Deal Create</SelectItem>
            <SelectItem value="deal.update">Deal Update</SelectItem>
            <SelectItem value="deal.delete">Deal Delete</SelectItem>
            <SelectItem value="payment.update">Payment Update</SelectItem>
            <SelectItem value="admin.impersonate">Impersonate</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="mt-2 text-muted-foreground">View all system events and user actions.</p>

      <FadeIn>
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 pr-4">Timestamp</th>
                <th className="py-2 pr-4">Actor</th>
                <th className="py-2 pr-4">Action</th>
                <th className="py-2 pr-4">Category</th>
                <th className="py-2 pr-4">Target</th>
                <th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-muted-foreground">
                    No audit logs found.
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <tr key={log.id} className="border-b hover:bg-muted/50">
                    <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4">
                      {log.actorName || log.actorEmail || log.actorId || 'System'}
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">{log.action}</td>
                    <td className="py-2 pr-4">{log.category}</td>
                    <td className="py-2 pr-4">{log.targetName || log.targetId || '--'}</td>
                    <td className="py-2">
                      {log.success ? (
                        <span className="inline-flex rounded-full px-2 py-0.5 text-xs bg-green-100 text-green-800">
                          OK
                        </span>
                      ) : (
                        <span className="inline-flex rounded-full px-2 py-0.5 text-xs bg-red-100 text-red-800">
                          Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </FadeIn>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => navigate({ search: { ...search, page: page - 1 } })}
          >
            Previous
          </Button>
          <span className="flex items-center text-sm text-muted-foreground px-3">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => navigate({ search: { ...search, page: page + 1 } })}
          >
            Next
          </Button>
        </div>
      )}
    </PageTransition>
  );
}
